import React from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { getReadbackComponent } from "@/readbacks";
import { Badge } from "@/components/ui/badge";

export default function ReadStory() {
  const { creationId } = useParams();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [creation, setCreation] = React.useState(null);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");

      const { data, error: qErr } = await supabase
        .from("creations")
        .select(`
          id,
          created_at,
          product_version_id,
          product_versions:product_version_id (
            id,
            slug,
            version,
            family_id,
            product_families:family_id ( title, type )
          )
        `)
        .eq("id", creationId)
        .single();

      if (qErr) throw qErr;
      if (!alive) return;

      setCreation(data);
    })()
      .catch((e) => {
        console.error(e);
        if (alive) setError(e?.message || "Could not load this story.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [creationId]);

  if (loading) return <div className="p-10 font-sans text-sm opacity-70">Loading…</div>;

  if (error) {
    return (
      <div className="p-10">
        <p className="font-sans text-sm text-red-600">{error}</p>
        <Button asChild variant="ghost" className="mt-4 rounded-xl">
          <Link to="/wallet">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to stories
          </Link>
        </Button>
      </div>
    );
  }

  const pv = creation?.product_versions;
  const familyTitle = pv?.product_families?.title;
  const familyType = pv?.product_families?.type; // BEST: use this as the key
  const slug = pv?.slug;
  const version = pv?.version

  const title = familyTitle || slug || "Story";
  const Readback = getReadbackComponent(familyType || slug);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <Button asChild variant="ghost" className="rounded-xl px-2">
        <Link to="/wallet">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to stories
        </Link>
      </Button>

      <h1 className="mt-4 font-kalice text-4xl font-medium items-center ">{title}<Badge className="ml-3 font-sans font-medium text-sm py-1 px-2 bg-white/80 text-black rounded-xl items-center align-middle hover:bg-white ">V{version}.0</Badge></h1>
      <p className="mt-2 font-sans text-sm text-muted-foreground">Creation ID: <span className="font-sans">{creation?.id}</span></p>

      {/* If no readback exists yet, show basic info */}
      {!Readback ? (
        <p className="mt-2 font-sans text-sm text-muted-foreground">
          No readback component registered for: <span className="font-mono">{familyType || slug}</span>
          <br />
          Creation ID: <span className="font-sans">{creation?.id}</span>
        </p>
      ) : (
        <React.Suspense fallback={<div className="mt-10 font-sans text-sm opacity-70">Loading readback…</div>}>
          <Readback creationId={creationId} creation={creation} />
        </React.Suspense>
      )}
    </div>
  );
}

